import Link from "next/link";
import Layout from "../../components/Layout";
import Head from "next/head";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import { BlogPostSkeleton } from "../../components/Skeleton";
import {
  fetchSingleBlog,
  fetchRelatedBlogs,
  formatBlogDate,
  shortenExcerpt,
  splitBodyAtMidpoint,
} from "../../helpers/blogUtils";
import RichTextRenderer from "./RichTextRenderer";

// ── placeholder related posts — replace with API data later ────────────────


const SHARE_LINKS = [
  {
    label: "Copy Link",
    type: "copy",
    url: "/images/icon/blog-link.svg",
  },
  {
    label: "WhatsApp",
    type: "share",
    url: "/images/icon/blog-whatsapp.svg",
    href: (url) => `https://wa.me/?text=${url}`,
  },
  {
    label: "Twitter / X",
    type: "share",
    url: "/images/icon/blog-twitter.svg",
    href: (url) => `https://x.com/intent/post?url=${url}`,
  },
  {
    label: "Facebook",
    type: "share",
    url: "/images/icon/blog-facebook.svg",
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
  },
  {
    label: "LinkedIn",
    type: "share",
    url: "/images/icon/blog-linkedin.svg",
    href: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
  },
];
function CategoryBadge({ label }) {
  return <span className="blog-category-badge">{label}</span>;
}

function RelatedPostCard({ post }) {
  return (
    <section>
       <Link href={`/blog/${post.slug}?id=${post.id}`}>
        <div className="blog-post-card" style={{cursor: "pointer"}}>
        <div className="blog-post-card__img-wrap">
          <img
            src={post.featured_image || "/images/kili-staffs.jpg"}
            alt={post.title.rendered}
            className="blog-post-card__img"
            loading="lazy"
            onError={(e) => {
              e.target.src =
                "https://placehold.co/400x220/f5f5f5/ccc?text=Image";
            }}
          />
        </div>
        <div className="blog-post-card__body">
         <CategoryBadge
  label={post.category
    .map((category) => category?.replace(/&amp;/g, "&"))
    .join(", ")}
/>
          <h6 className="blog-post-card__title mt-2">{post.title.replace(/&#038;/g, "&")}</h6>
           <RichTextRenderer
                                    html={shortenExcerpt(post.excerpt.rendered, 180)}
                                    className="blog-post-page__content"
                                  />
          <p className="blog-post-card__meta">
            {formatBlogDate(post.date)} &bull;{" "}
            <span className="blog-post-card__name">{post.author_name}</span>
          </p>
          
            <a className="blog-read-more mt-2">
              Read More{" "}
              <span>
                <img
                  src="/images/icon/blog-arrow-right.svg"
                  alt="arrow right"
                  className="blog-arrow-right-icon"
                />
              </span>
            </a>
          
        </div>
      </div>
       </Link>
      
    </section>
  );
}

export function CtaBanner() {
  return (
    <section>
      <div className="blog-cta-banner ">
        <div className="row align-items-stretch no-gutters">
          {" "}
          {/* align-items-stretch so both cols are full height */}
          <div className="col-md-7 col-lg-8 blog-cta-banner__content d-flex flex-column justify-content-center">
            <h6 className="blog-cta-banner__title">Ready for a Good Meal?</h6>
            <p className="blog-cta-banner__text">
              Find your favourite dishes at Kilimanjaro Restaurants near you or
              order online for pickup or delivery.
            </p>
            <div className="d-flex flex-wrap gap-2 mt-3">
              {/* <Link href="/store-locations">
                <a className="blog-btn-white mr-2">Explore Our Menu</a>
              </Link> */}
              <Link href="/menu">
                <a className="blog-btn-white ml-2">Order Now</a>
              </Link>
            </div>
          </div>
          <div className="col-md-5 col-lg-4 d-none d-md-flex blog-cta-banner__image-col">
            <img
              src="/images/food-layer.webp"
              alt="Kilimanjaro food"
              className="blog-cta-banner__img"
              onError={(e) => {
                e.target.src = "https://placehold.co/300x200/fff/ccc?text=Food";
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── main post page ──────────────────────────────────────────────────────────
export default function BlogPost() {
  const router = useRouter();
  const { id } = router.query;
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
 
 


  const loadPost = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchSingleBlog(id);
      

      setPost(data);
    } catch (err) {
      console.error("Failed to fetch blog post", err);
      setError("Failed to load this post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  

    useEffect(() => {
    if (!router.isReady || !id) return;

    loadPost();
    
  }, [router.isReady, id]);

 
  const { first: bodyFirst, second: bodySecond } = post?.content.rendered
    ? splitBodyAtMidpoint(post.content.rendered)
    : { first: "", second: "" };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

 


  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);

      setLinkCopied(true);

      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

   if (isLoading) return <BlogPostSkeleton />;
     if (error) {
    return (
      <div className="container blog-post-container text-center py-5">
        <p className="text-danger">{error}</p>
        <Link href="/blog">
          <a className="blog-btn-primary mt-3 d-inline-block">
            ← Back to Blog
          </a>
        </Link>
      </div>
    );
  }

  return (
    <Layout>
      <Head>
        {/* <link rel="canonical" href={canonicalUrl} /> */}
        <title>{post?.seo_title}</title>
        <meta
          name="description"
          content={post?.seo_description}
        />

        <meta name="keyword" content="MEALS FOOD RESTAURANT NIGERIA" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              url: "https://kilimanjaro-restaurants.com",
              name: "Kilimanjaro",
              description:
                "Kilimanjaro is one of the Sundry Foods’ restaurant brands, operating in the Quick Service Restaurant (QSR) segment, the brand has established itself as a market leader and one of the fastest-growing restaurant brands in the country with currently 59 stores across Nigeria and more to come.Kilimanjaro caters to the unique preference of the general populace in this part of sub-Saharan Africa for their local dishes. Our unique menu consists of select popular contemporary and Nigerian offerings. Items on our menu can also be found on dining tables in other African countries. The Kilimanjaro brand was born out of the quest to satisfy both the local and continental food cravings of our people. We deliver fresh, hot, and mouth-watering meals, pastries, sandwiches, and beverages to thousands of customers in the finest environment each day. Our restaurants stand out for their bright colors and lovely ambiance inviting you to dine-in or grab-and-go with the best quality meals.",
              publisher: {
                "@type": "Organization",
                name: "Kilimanjaro",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "0700 5454 3663",
                contactType: "customer support",
              },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Kilimanjaro",
              url: "https://kilimanjaro-restaurants.com",
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "0700 5454 3663",
                contactType: "customer support",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "23 Nzimiro street, Old GRA. Port Harcourt",
                addressLocality: "Port Harcourt",
                addressRegion: "Rivers",
                addressCountry: "Nigeria",
              },
            }),
          }}
        />
      </Head>
      <>
        <section className="blog-post-page">
          <div className="container blog-post-container">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8">
                {/* category + title */}
                <CategoryBadge
  label={post.category_name
    .map((category) => category.replace(/&amp;/g, "&"))
    .join(", ")}
/>
                <h1 className="blog-post-page__title mt-2 mb-0">
                  {post.title.rendered.replace(/&#038;/g, "&")}
                </h1>

                {/* meta */}
                <h4 className="blog-post-page__meta mb-3">
                  {post.author_name} &bull; {formatBlogDate(post.date)}
                </h4>

               {post.yoast_head_json?.og_image[0]?.url  && (
              <div className="blog-post-page__hero-wrap my-4">
                <img
                  src={post.yoast_head_json?.og_image[0]?.url }
                  alt={post.title.rendered}
                  className="blog-post-page__hero"
                  onError={(e) => {
                    e.target.src =
                      "https://placehold.co/800x450/f5f5f5/ccc?text=Image";
                  }}
                />
              </div>
            )}
                

             
              <RichTextRenderer
                html={post.content.rendered}
                className="blog-post-page__content"
              />
            

                
                 
             

                {/* share */}
                <div className="blog-post-page__share mt-5">
                  <span className="blog-post-page__share-label">Share:</span>

                  {SHARE_LINKS.map((link) => {
                    if (link.type === "copy") {
                      return (
                        <button
                          key={link.label}
                          type="button"
                          onClick={handleCopyLink}
                          className="blog-share-btn"
                          aria-label="Copy link"
                        >
                          <img src={link.url} alt="" className="social-icon" />

                          {linkCopied && (
                            <span className="copy-feedback">Copied!</span>
                          )}
                        </button>
                      );
                    }

                    return (
                      <a
                        key={link.label}
                        href={link.href(encodeURIComponent(pageUrl))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="blog-share-btn"
                        aria-label={`Share on ${link.label}`}
                      >
                        <img
                          src={link.url}
                          alt={link.label}
                          className="social-icon"
                        />
                      </a>
                    );
                  })}
                 

                </div>
                 <CtaBanner />
              </div>
              
            </div>

           
           

            {/* related posts */}
            {
              post.related_posts.length > 0 && (
                 <div className="blog-related-section mt-2 ">
              <h6 className="blog-related-section__title mb-4">
                Read more articles
              </h6>
              <div className="blog-post-grid">
                {post.related_posts.map((relatedPost) => (
                  <RelatedPostCard key={relatedPost.id} post={relatedPost} />
                ))}
              </div>
            </div>
              )
            }
           
          </div>
          
        </section>
      </>
    </Layout>
  );
}
